import { useMemo } from 'react';
import { SLING_PREMIUM, SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { PremiumFeatureAccessLevel } from '@sdk/authentication/types';

import { getFeatureAccessLevel, getSlingFeatures } from '@web/account/utils';
import {
  businessFeatureDefinitions,
  PremiumFeatureDescription,
  premiumFeatureDescription,
} from '@web/premiumGuard/constants/premiumFeaturesDescription';
import { FeatureNames } from '@web/account/types';

type Props = {
  currentUser: AuthUserType;
  hiddenKeys: FeatureNames[];
  featureAccessLevel: PremiumFeatureAccessLevel;
};

const useGetPaidFeatureList = ({ currentUser, hiddenKeys, featureAccessLevel }: Props) => {
  const featureList = useMemo<PremiumFeatureDescription[]>(() => {
    const { orgSettings } = currentUser;
    const slingFeatures = getSlingFeatures(orgSettings, orgSettings.isOldPremium);

    // @INFO new business tier
    if (featureAccessLevel === SUBSCRIPTION_LEVELS.BUSINESS) {
      return businessFeatureDefinitions;
    }

    return premiumFeatureDescription.filter(({ key }) => {
      if (hiddenKeys.includes(key)) {
        return false;
      }

      // Some features exists only "on paper", for marketing purposes.
      // For example Budgeting and Overtime tracking both refer to laborCost under the hood.
      // When feature is fakish it will be present in slingFeatures, that's why we check it first and if we can't find it there we use regular getFeatureAccessLevel method.
      const accessLevel =
        typeof slingFeatures[key] !== 'undefined'
          ? slingFeatures[key]
          : getFeatureAccessLevel(key, orgSettings);

      if (orgSettings.isOldPremium) {
        return accessLevel >= SLING_PREMIUM;
      }

      return accessLevel === featureAccessLevel;
    });
  }, [currentUser, featureAccessLevel, hiddenKeys]);

  return featureList;
};

export default useGetPaidFeatureList;
